import { useState, useEffect, useCallback, useRef } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  Loader2, Plus, Trash2, Edit2, ArrowRight, 
  AlertTriangle, CheckCircle2, Search, RefreshCw, X, BarChart2, Zap, AlertCircle,
  Upload, Download, FileDown
} from "lucide-react";
import axios from "axios";

const AdminRedirects = () => {
  const { token } = useAuth();
  const [redirects, setRedirects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingRedirect, setEditingRedirect] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [validating, setValidating] = useState(false);
  const [destinationValidation, setDestinationValidation] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);
  
  const [form, setForm] = useState({
    source_url: "",
    destination_url: "",
    redirect_type: "301",
    is_active: true,
    notes: ""
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchRedirects = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/redirects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRedirects(response.data);
    } catch (error) {
      toast.error("Failed to fetch redirects");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL, token]);

  useEffect(() => {
    fetchRedirects();
  }, [fetchRedirects]);

  // Validate destination URL when it changes
  const validateDestination = async (url) => {
    if (!url || url.length < 2) {
      setDestinationValidation(null);
      return;
    }
    
    setValidating(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/redirects/validate-destination?destination_url=${encodeURIComponent(url)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDestinationValidation(response.data);
    } catch (error) {
      setDestinationValidation({ valid: true, exists: false, warning: "Could not validate" });
    } finally {
      setValidating(false);
    }
  };

  // Debounce destination validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (form.destination_url) {
        validateDestination(form.destination_url);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [form.destination_url]);

  const resetForm = () => {
    setForm({
      source_url: "",
      destination_url: "",
      redirect_type: "301",
      is_active: true,
      notes: ""
    });
    setEditingRedirect(null);
    setDestinationValidation(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.source_url || !form.destination_url) {
      toast.error("Both source and destination URLs are required");
      return;
    }
    
    if (form.source_url === form.destination_url) {
      toast.error("Source and destination URLs cannot be the same");
      return;
    }
    
    setSaving(true);
    try {
      if (editingRedirect) {
        await axios.put(
          `${BACKEND_URL}/api/redirects/${editingRedirect.id}`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Redirect updated successfully");
      } else {
        await axios.post(
          `${BACKEND_URL}/api/redirects`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Redirect created successfully");
      }
      
      setShowModal(false);
      resetForm();
      fetchRedirects();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save redirect");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (redirect) => {
    setEditingRedirect(redirect);
    setForm({
      source_url: redirect.source_url,
      destination_url: redirect.destination_url,
      redirect_type: redirect.redirect_type,
      is_active: redirect.is_active,
      notes: redirect.notes || ""
    });
    setShowModal(true);
  };

  const handleDelete = async (redirect) => {
    if (!window.confirm(`Delete redirect from ${redirect.source_url}?`)) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/redirects/${redirect.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Redirect deleted");
      fetchRedirects();
    } catch (error) {
      toast.error("Failed to delete redirect");
    }
  };

  const handleToggleActive = async (redirect) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/redirects/${redirect.id}`,
        { is_active: !redirect.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(redirect.is_active ? "Redirect disabled" : "Redirect enabled");
      fetchRedirects();
    } catch (error) {
      toast.error("Failed to update redirect");
    }
  };

  const filteredRedirects = redirects.filter(r => 
    r.source_url.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.destination_url.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (r.notes && r.notes.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const getRedirectTypeColor = (type) => {
    switch(type) {
      case "301": return "bg-green-100 text-green-700";
      case "302": return "bg-blue-100 text-blue-700";
      case "307": return "bg-amber-100 text-amber-700";
      case "308": return "bg-purple-100 text-purple-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const getRedirectTypeLabel = (type) => {
    switch(type) {
      case "301": return "301 Permanent";
      case "302": return "302 Temporary";
      case "307": return "307 Temp (Preserve Method)";
      case "308": return "308 Perm (Preserve Method)";
      default: return type;
    }
  };

  // CSV Upload Handler
  const handleCSVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error("Please upload a CSV file");
      return;
    }
    
    setUploading(true);
    setUploadResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/redirects/bulk-upload-csv`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      setUploadResult(response.data);
      
      if (response.data.created > 0 || response.data.updated > 0) {
        toast.success(`Created ${response.data.created}, Updated ${response.data.updated} redirects`);
        fetchRedirects();
      } else if (response.data.total_errors > 0) {
        toast.warning(`Upload completed with ${response.data.total_errors} errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload CSV");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Download Template
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/redirects/download-template`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'redirect_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  // Export Redirects
  const handleExportRedirects = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/redirects/export`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `redirects_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Redirects exported successfully");
    } catch (error) {
      toast.error("Failed to export redirects");
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-redirects">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-semibold text-foreground mb-1 sm:mb-2">
                URL Redirections
              </h1>
              <p className="text-sm text-muted-foreground">Patch-based redirects - changes apply instantly</p>
            </div>
            {/* Mobile: Show only Add button, rest in overflow */}
            <div className="flex sm:hidden gap-2">
              <Button 
                onClick={() => { resetForm(); setShowModal(true); }}
                className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="add-redirect-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Redirect
              </Button>
            </div>
          </div>
          
          {/* Action Buttons - Desktop: inline, Mobile: grid */}
          <div className="flex flex-wrap gap-2">
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv"
              onChange={handleCSVUpload}
              className="hidden"
            />
            <Button 
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
              className="rounded-full text-xs sm:text-sm"
            >
              <Download className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Download Template</span>
            </Button>
            <Button 
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="rounded-full bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 text-xs sm:text-sm"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 sm:mr-2" />
              )}
              <span className="hidden sm:inline">Bulk Upload CSV</span>
            </Button>
            <Button 
              variant="outline"
              size="sm"
              onClick={handleExportRedirects}
              className="rounded-full text-xs sm:text-sm"
            >
              <FileDown className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Export All</span>
            </Button>
            <Button 
              onClick={() => { resetForm(); setShowModal(true); }}
              className="hidden sm:flex rounded-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="add-redirect-btn-desktop"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Redirect
            </Button>
          </div>
        </div>

        {/* Upload Result Banner */}
        {uploadResult && (
          <div className={`rounded-xl p-4 ${uploadResult.total_errors > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium text-sm mb-2">
                  {uploadResult.total_errors > 0 ? '⚠️ Upload Completed with Warnings' : '✅ Upload Successful'}
                </p>
                <div className="flex gap-6 text-sm">
                  <span className="text-green-700">Created: <strong>{uploadResult.created}</strong></span>
                  <span className="text-blue-700">Updated: <strong>{uploadResult.updated}</strong></span>
                  <span className="text-gray-600">Skipped: <strong>{uploadResult.skipped}</strong></span>
                  {uploadResult.total_errors > 0 && (
                    <span className="text-red-700">Errors: <strong>{uploadResult.total_errors}</strong></span>
                  )}
                </div>
                {uploadResult.errors?.length > 0 && (
                  <div className="mt-2 text-xs text-red-600">
                    {uploadResult.errors.slice(0, 5).map((err, i) => (
                      <p key={i}>{err}</p>
                    ))}
                    {uploadResult.errors.length > 5 && (
                      <p>... and {uploadResult.errors.length - 5} more errors</p>
                    )}
                  </div>
                )}
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setUploadResult(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Info Banner - Patch-Based System - Hidden on mobile */}
        <div className="hidden sm:block bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded-xl p-4">
          <div className="flex gap-3">
            <Zap className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-800">
              <p className="font-medium mb-1">Patch-Based Redirects (No Rebuild Required)</p>
              <p className="text-emerald-700">Changes apply <strong>immediately</strong> without affecting meta tags, GA, or GSC configuration.</p>
              <ul className="list-disc list-inside mt-2 text-emerald-700">
                <li><strong>301:</strong> Permanent redirect - use when URL has permanently moved</li>
                <li><strong>302:</strong> Temporary redirect - use for short-term changes</li>
                <li>Redirected URLs are automatically excluded from sitemap</li>
                <li>Destination URLs are validated before saving</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Search & Stats */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search redirects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-redirects"
            />
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <BarChart2 className="w-4 h-4" />
              {redirects.length} total redirects
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              {redirects.filter(r => r.is_active).length} active
            </span>
          </div>
        </div>

        {/* Redirects List */}
        <div className="bg-white rounded-xl shadow-soft overflow-hidden">
          {filteredRedirects.length === 0 ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No redirects found</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery ? "Try a different search term" : "Create your first redirect to manage URL changes"}
              </p>
              {!searchQuery && (
                <Button onClick={() => { resetForm(); setShowModal(true); }} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Redirect
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {filteredRedirects.map((redirect) => (
                <div 
                  key={redirect.id} 
                  className={`p-3 sm:p-4 hover:bg-gray-50 transition-colors ${!redirect.is_active ? 'opacity-60 bg-gray-50' : ''}`}
                  data-testid={`redirect-${redirect.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRedirectTypeColor(redirect.redirect_type)}`}>
                          {redirect.redirect_type}
                        </span>
                        {!redirect.is_active && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                            Disabled
                          </span>
                        )}
                        {redirect.hit_count > 0 && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                            {redirect.hit_count} hits
                          </span>
                        )}
                        {redirect.destination_validated && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            Verified
                          </span>
                        )}
                        {redirect.destination_warning && !redirect.destination_validated && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            Unverified
                          </span>
                        )}
                      </div>
                      
                      {/* Mobile: Stack URLs vertically, Desktop: Inline */}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm">
                        <code className="bg-red-50 text-red-700 px-2 py-1 rounded font-mono text-xs break-all sm:truncate sm:max-w-[300px]">
                          {redirect.source_url}
                        </code>
                        <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0 rotate-90 sm:rotate-0 mx-auto sm:mx-0" />
                        <code className="bg-green-50 text-green-700 px-2 py-1 rounded font-mono text-xs break-all sm:truncate sm:max-w-[300px]">
                          {redirect.destination_url}
                        </code>
                      </div>
                      
                      {redirect.destination_warning && (
                        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {redirect.destination_warning}
                        </p>
                      )}
                      
                      {redirect.notes && (
                        <p className="text-xs text-muted-foreground mt-2 line-clamp-2 sm:truncate">
                          {redirect.notes}
                        </p>
                      )}
                    </div>
                    
                    {/* Action buttons - always visible */}
                    <div className="flex items-center gap-1 sm:gap-2 justify-end sm:justify-start">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(redirect)}
                        className={`h-8 w-8 p-0 ${redirect.is_active ? "text-green-600" : "text-gray-400"}`}
                        data-testid={`toggle-redirect-${redirect.id}`}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(redirect)}
                        className="h-8 w-8 p-0"
                        data-testid={`edit-redirect-${redirect.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(redirect)}
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                        data-testid={`delete-redirect-${redirect.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold">
                    {editingRedirect ? "Edit Redirect" : "Create Redirect"}
                  </h2>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => { setShowModal(false); resetForm(); }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Source URL (Old URL) <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={form.source_url}
                      onChange={(e) => setForm({ ...form, source_url: e.target.value })}
                      placeholder="/homestay/old-slug"
                      data-testid="redirect-source-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      The URL path that will redirect (e.g., /homestay/old-name)
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Destination URL (New URL) <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Input
                        value={form.destination_url}
                        onChange={(e) => setForm({ ...form, destination_url: e.target.value })}
                        placeholder="/homestay/new-slug"
                        data-testid="redirect-destination-input"
                        className={destinationValidation?.exists ? "border-green-500" : destinationValidation?.warning ? "border-amber-500" : ""}
                      />
                      {validating && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                      )}
                    </div>
                    
                    {/* Destination Validation Feedback */}
                    {destinationValidation && !validating && (
                      <div className={`mt-2 p-2 rounded text-xs flex items-center gap-2 ${
                        destinationValidation.exists 
                          ? "bg-green-50 text-green-700" 
                          : destinationValidation.warning 
                            ? "bg-amber-50 text-amber-700"
                            : "bg-gray-50 text-gray-600"
                      }`}>
                        {destinationValidation.exists ? (
                          <>
                            <CheckCircle2 className="w-4 h-4" />
                            Destination URL verified - page exists
                          </>
                        ) : destinationValidation.warning ? (
                          <>
                            <AlertCircle className="w-4 h-4" />
                            {destinationValidation.warning}
                          </>
                        ) : null}
                      </div>
                    )}
                    
                    <p className="text-xs text-muted-foreground mt-1">
                      Where the user will be redirected to
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1 block">Redirect Type</label>
                    <Select 
                      value={form.redirect_type} 
                      onValueChange={(value) => setForm({ ...form, redirect_type: value })}
                    >
                      <SelectTrigger data-testid="redirect-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="301">301 - Permanent Redirect (Recommended)</SelectItem>
                        <SelectItem value="302">302 - Temporary Redirect</SelectItem>
                        <SelectItem value="307">307 - Temporary (Preserve Method)</SelectItem>
                        <SelectItem value="308">308 - Permanent (Preserve Method)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Use 301 for permanent URL changes, 302 for temporary
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1 block">Notes (Optional)</label>
                    <Input
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Reason for redirect..."
                      data-testid="redirect-notes-input"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="is_active"
                      checked={form.is_active}
                      onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                      data-testid="redirect-active-checkbox"
                    />
                    <label htmlFor="is_active" className="text-sm font-medium cursor-pointer">
                      Active (redirect is enabled)
                    </label>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setShowModal(false); resetForm(); }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={saving}
                      className="flex-1"
                      data-testid="save-redirect-btn"
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : null}
                      {editingRedirect ? "Update Redirect" : "Create Redirect"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminRedirects;
